// The outcome of an integration OAuth authorization, carried back from core's
// callback as query parameters on a full-page redirect.
//
// Mirrors `OAuthOutcome` in apps/api/core/src/integrations/oauth.
export type OAuthReturnOutcome = "connected" | "reconnected" | "denied" | "failed";

export interface OAuthReturn {
  provider: string;
  outcome: OAuthReturnOutcome;
}

const OUTCOMES: OAuthReturnOutcome[] = ["connected", "reconnected", "denied", "failed"];

let captured: OAuthReturn | null = null;

/**
 * Read the OAuth outcome off the URL and hold it, ONCE, before the router runs.
 *
 * This is captured at app entry rather than in the component that consumes it,
 * and that is the whole point. Core redirects to the app ROOT (it knows the
 * workspace id, not the URL path, so it cannot deep-link). `/` immediately
 * redirects to the user's default route via TanStack `<Navigate>`, which does not
 * carry search params — and `/$workspace/` may redirect a second time. Any route
 * declaring `validateSearch` strips unknown params on the way through as well.
 * Meanwhile `IntegrationsDialog`, which consumes the outcome, is mounted only
 * under `/$workspace`, so its first effect runs AFTER all of that. Reading the
 * URL there finds nothing, and the admin sees no result from an authorization
 * that actually succeeded.
 *
 * Capturing here sidesteps routing entirely: no dependence on which default
 * route the user has, no `retainSearchParams` middleware, and no per-route
 * search schema to keep in step.
 *
 * Also strips the parameters from the address bar so a refresh cannot re-report
 * a stale outcome, using `replaceState` rather than a router navigation because
 * the router does not exist yet.
 */
export function captureOAuthReturn(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const outcome = params.get("oauth");
  const provider = params.get("integration");
  if (!outcome) return;

  if (OUTCOMES.includes(outcome as OAuthReturnOutcome)) {
    captured = { provider: provider ?? "", outcome: outcome as OAuthReturnOutcome };
  }

  params.delete("oauth");
  params.delete("integration");
  const query = params.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
  );
}

/**
 * Take the captured outcome, clearing it. Returns null when this load did not
 * come back from an authorization, or when something already took it.
 *
 * Take-once, so a consumer that remounts (React StrictMode double-invokes
 * effects in development) cannot report the same authorization twice.
 */
export function takeOAuthReturn(): OAuthReturn | null {
  const value = captured;
  captured = null;
  return value;
}
